import { ChevronLeft, ChevronRight, X } from "lucide-react";
import { Dialog } from "radix-ui";
import type { ProductImage } from "@estelle/domain";

import { cn } from "@/lib/utils";

const NAV_BUTTON = cn(
  "bg-background/90 text-foreground hover:bg-accent absolute top-1/2 -translate-y-1/2",
  "rounded-full border p-2 shadow-sm transition-colors",
  "focus-visible:ring-ring focus-visible:ring-2 focus-visible:outline-none",
);

/**
 * A product's gallery, one image at a time, over the page.
 *
 * Controlled by index rather than by an open flag: which image is showing is
 * the only state there is, and `null` is closed. Arrow keys and the buttons
 * wrap at either end, so the last image leads back to the first instead of to
 * a disabled button.
 *
 * A single image gets the same dialog without the arrows, counter or strip —
 * those would be controls that do nothing.
 *
 * Radix's Dialog nests inside the drawer's own, so Escape closes this one and
 * leaves the drawer open underneath.
 */
export function ImageLightbox({
  images,
  title,
  index,
  onIndexChange,
}: {
  images: ProductImage[];
  title: string;
  index: number | null;
  onIndexChange: (index: number | null) => void;
}) {
  const count = images.length;
  const current = index === null ? undefined : images[index];
  const several = count > 1;

  const step = (by: number) => {
    if (index === null) return;
    onIndexChange((index + by + count) % count);
  };

  return (
    <Dialog.Root
      open={current !== undefined}
      onOpenChange={(open) => {
        if (!open) onIndexChange(null);
      }}
    >
      <Dialog.Portal>
        <Dialog.Overlay className="data-[state=open]:animate-in data-[state=open]:fade-in-0 fixed inset-0 z-50 bg-black/70 motion-reduce:animate-none" />
        <Dialog.Content
          className="bg-background data-[state=open]:animate-in data-[state=open]:fade-in-0 data-[state=open]:zoom-in-95 fixed inset-4 z-50 flex flex-col overflow-hidden rounded-lg border shadow-lg motion-reduce:animate-none sm:inset-10"
          onKeyDown={(event) => {
            if (!several) return;
            if (event.key === "ArrowRight") step(1);
            if (event.key === "ArrowLeft") step(-1);
          }}
        >
          <div className="flex shrink-0 items-center gap-3 border-b px-4 py-3">
            <Dialog.Close className="text-muted-foreground hover:bg-accent hover:text-foreground focus-visible:ring-ring -ml-1 rounded-md p-1.5 transition-colors focus-visible:ring-2 focus-visible:outline-none">
              <X className="size-4" aria-hidden />
              <span className="sr-only">Close</span>
            </Dialog.Close>
            <Dialog.Title className="min-w-0 flex-1 truncate text-base font-semibold tracking-tight">
              {title}
            </Dialog.Title>
            <Dialog.Description
              className={cn(
                "text-muted-foreground shrink-0 text-sm tabular-nums",
                !several && "sr-only",
              )}
            >
              Image {index === null ? 0 : index + 1} of {count}
            </Dialog.Description>
          </div>

          <div className="bg-muted/30 relative flex min-h-0 flex-1 items-center justify-center p-4">
            {current ? (
              <img
                key={current.id}
                src={current.url}
                alt={current.altText ?? title}
                className="max-h-full max-w-full object-contain"
              />
            ) : null}

            {several ? (
              <>
                <button
                  type="button"
                  className={cn(NAV_BUTTON, "left-4")}
                  onClick={() => {
                    step(-1);
                  }}
                >
                  <ChevronLeft className="size-5" aria-hidden />
                  <span className="sr-only">Previous image</span>
                </button>
                <button
                  type="button"
                  className={cn(NAV_BUTTON, "right-4")}
                  onClick={() => {
                    step(1);
                  }}
                >
                  <ChevronRight className="size-5" aria-hidden />
                  <span className="sr-only">Next image</span>
                </button>
              </>
            ) : null}
          </div>

          {several ? (
            <div className="flex shrink-0 justify-center gap-2 overflow-x-auto border-t p-3">
              {images.map((image, position) => (
                <button
                  key={image.id}
                  type="button"
                  aria-label={`Image ${String(position + 1)}`}
                  aria-current={position === index}
                  onClick={() => {
                    onIndexChange(position);
                  }}
                  className={cn(
                    "focus-visible:ring-ring shrink-0 overflow-hidden rounded-md border-2 transition-opacity focus-visible:ring-2 focus-visible:outline-none",
                    position === index
                      ? "border-primary"
                      : "border-transparent opacity-60 hover:opacity-100",
                  )}
                >
                  <img
                    src={image.url}
                    alt=""
                    loading="lazy"
                    className="size-16 object-cover"
                  />
                </button>
              ))}
            </div>
          ) : null}
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
